import "./H001.scss";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import {
  FCFacilitiesSchema,
  GeneralForListSchema,
  ChildSchema,
  FCStaffInfoSchema,
  TrainingH001Schema,
} from "../../generated";
import { authSelector, userInfoSelector } from "../../redux/selectors/auth.selector";
import {
  fetchFcFacilities,
  fetchChildrenForSelectList,
  fetchH001,
  fetchGeneralsForListMulti,
  fetchH001Count,
} from "../../services/api.service";
import Select from "../../components/Select/CustomSelect";
import { useLoadingIndicator } from "../../hooks/LoadingIndicator.hooks";
import LoadingSpinner from "../../components/Loading/LoadingSpinner";
import CustomButton, { CB_CLASS } from "../../components/Button/CustomButton"
import CustomDate from "../../components/Calendar/CustomDate"
import Title from "../../components/Title/Title"
import Chat from "../../components/Icon/Chat"
import User from "../../components/Pict/User"
import Status, { STATUS_RED, STATUS_BLUE, STATUS_GREEN, StatusSchema, STATUS_GRAY, STATUS_WHITE } from "../../components/Status/Status"
import {
  TRAINING_STATUS_NOT_TRAINED,
  TRAINING_STATUS_TRAINED,
  TRAINING_STATUS_SIGNED,
  TRAINING_STATUS_SENT,
  SERVICE_PROVISION_STATUS,
  TRAINING_STATUS_NOT_PLANNED,
} from "../../models/training.model";
import { initialGeneralForList } from "../../models/general.model";
import { LIST_TRAINING_STATUS, HEADQUARTER_USER, STAFF, LIST_SERVICE_PROVISION_STATUS, PARENT } from "../../constants";
import { C002Url, D002Url, G001Url, H002Url, H003Url, getForHistoryPush } from "../../helper/url.helper";
import { getState, pushWithModifier } from "../../helper/page.helper";
import { makeStringDateWithSlash } from "../../helper/time.helper";
import { useAuth } from "../../helper/auth.helper";
import moment from "moment";
import { notifyError } from "../../helper/settings.helper";
import PaginationTableLite from "../../components/PaginationTableLite/PaginationTableLite";
import { fcListSelector } from "../../redux/selectors/list.selector";
import { listActions } from "../../redux/actions/list.actions";
import { FC_LIST_TYPE, getFcList, getFcListForRedux, getFcListFromRedux } from "../../helper/list.helper";

interface iLocation {
  child_id: number;
  facility_id: number;
  begin_date: string;
  end_date: string;
}

const H001 = () => {
  const { checkLoggedIn } = useAuth();
  const maxWidth = 1300;
  const location = useLocation<iLocation>();
  const state = getState(location);
  const authState = useSelector(authSelector);
  const history = useHistory();
  const userInfo = useSelector(userInfoSelector);
  const facilityId = state.facility_id ? state.facility_id : userInfo?.user_type == STAFF ? (userInfo as FCStaffInfoSchema)?.facilities[0].facility_id : 0;
  const [data, setData] = useState<TrainingH001Schema[]>([]);
  const [fcForSelect, setFcForSelect] = useState<GeneralForListSchema[]>([]);
  const [childForSelect, setChildForSelect] = useState<GeneralForListSchema[]>([]);
  const [getChildForSelect, setGetChildForSelect] = useState(true);
  const [statusForSelect, setStatusForSelect] = useState<GeneralForListSchema[]>([]);
  const [absenceTypeForSelect, setAbsenceTypeForSelect] = useState<GeneralForListSchema[]>([]);
  const [getGeneral, setGetGeneral] = useState(true);
  const [isLoading, , startLoading, stopLoading] = useLoadingIndicator();
  const [search, setSearch] = useState({
    facility_id: facilityId,
    child_id: state.child_id,
    begin_date: state.begin_date || moment().subtract(1, "months").format("yyyy-MM-DD"),
    end_date: state.end_date || "",
    status: 0,
    absence_type: 0,
  });
  const status_list: StatusSchema[] = [
    { num: TRAINING_STATUS_NOT_PLANNED, class: STATUS_RED },
    { num: TRAINING_STATUS_NOT_TRAINED, class: STATUS_BLUE },
    { num: TRAINING_STATUS_TRAINED, class: STATUS_GREEN },
    { num: TRAINING_STATUS_SENT, class: STATUS_GRAY },
    { num: TRAINING_STATUS_SIGNED, class: STATUS_WHITE },
  ];
  const [currentPage, setCurrentPage] = useState(1);
  const [recordCount, setRecordCount] = useState(0);
  const limitCount = 10;
  const fcListState = useSelector(fcListSelector);
  const dispatch = useDispatch();
  const moveByClick = (e: React.MouseEvent, routeData: any) => pushWithModifier(e, history, routeData);
  useEffect(() => {

    let cancelled = false;
    const fetchData = async () => {
      startLoading();
      try {
        const [resCount, resData] = await Promise.all([
          fetchH001Count(authState, search.facility_id, search.child_id, search.begin_date, search.end_date, search.status, search.absence_type),
          fetchH001(authState, search.facility_id, search.child_id, search.begin_date, search.end_date, search.status, search.absence_type, (currentPage - 1) * limitCount, limitCount)
        ]);

        if (!cancelled) {
          setRecordCount(resCount.data);
          setData(resData.data);
        }
      } catch (err) {
        if (!cancelled) {
          checkLoggedIn(err);
          notifyError("データの取得に失敗しました。");
          console.error(err);
        }
      } finally {
        if (!cancelled) {
          stopLoading();
        }
      }
    };

    fetchData();
    
    return () => {
      cancelled = true;
    };
  }, [authState, checkLoggedIn, search, currentPage, startLoading, stopLoading]);

  useEffect(() => {
    if (userInfo?.user_type !== HEADQUARTER_USER) return;
    if (fcForSelect.length > 0) return;

    let cancelled = false;
    
    if (fcListState.select_list.length > 0) {
      setFcForSelect(getFcListFromRedux(FC_LIST_TYPE.ADD_INIT, fcListState.select_list));
      return;
    }

    const fetchData = async () => {
      startLoading();
      try {
        const res = await fetchFcFacilities(authState, "");
        if (!cancelled) {
          setFcForSelect(getFcList(FC_LIST_TYPE.ADD_INIT, res.data));
          dispatch(listActions.makeFcList(getFcListForRedux(res.data)));
        }
      } catch (err) {
        if (!cancelled) {
          notifyError("データの取得に失敗しました。");
          console.error(err);
        }
      } finally {
        if (!cancelled) {
          stopLoading();
        }
      }
    };

    fetchData();
    
    return () => {
      cancelled = true;
    };
  }, [authState, dispatch, checkLoggedIn, userInfo, fcForSelect.length, fcListState.select_list, startLoading, stopLoading]);

  useEffect(() => {
    if (!getGeneral) return;

    let cancelled = false;
    const fetchData = async () => {
      startLoading();
      try {
        const keywords = [LIST_TRAINING_STATUS, LIST_SERVICE_PROVISION_STATUS];
        const res = await fetchGeneralsForListMulti(authState, keywords);
        if (!cancelled) {
          keywords.map(x => res.data[x].unshift(initialGeneralForList()));
          setStatusForSelect(res.data[LIST_TRAINING_STATUS]);
          setAbsenceTypeForSelect(res.data[LIST_SERVICE_PROVISION_STATUS]);
          setGetGeneral(false);
        }
      } catch (err) {
        if (!cancelled) {
          checkLoggedIn(err);
          notifyError("データの取得に失敗しました。");
          console.error(err);
        }
      } finally {
        if (!cancelled) {
          stopLoading();
        }
      }
    };

    fetchData();
    
    return () => {
      cancelled = true;
    };
  }, [authState, checkLoggedIn, getGeneral, startLoading, stopLoading]);

  useEffect(() => {
    if (search.facility_id <= 0 || !getChildForSelect) return;

    let cancelled = false;
    const fetchData = async () => {
      startLoading();
      try {
        const res = await fetchChildrenForSelectList(authState, search.facility_id);
        if (!cancelled) {
          const forSelect: GeneralForListSchema[] = [initialGeneralForList()];
          res.data.map((x: ChildSchema, i) => {
            forSelect.push({
              value: x.id,
              label: x.family_name + x.given_name
            });
          });
          setChildForSelect(forSelect);
          setGetChildForSelect(false);
        }
      } catch (err) {
        if (!cancelled) {
          checkLoggedIn(err);
          notifyError("データの取得に失敗しました。");
          console.error(err);
        }
      } finally {
        if (!cancelled) {
          stopLoading();
        }
      }
    };

    fetchData();
    
    return () => {
      cancelled = true;
    };
  }, [authState, checkLoggedIn, search, getChildForSelect, startLoading, stopLoading]);

  const getSelectValue = (val: number, selectList: GeneralForListSchema[]) => {
    if (val == 0) return null;
    return selectList.filter(x => x.value == val)[0];
  };

  const changeCurrentPage = (v: number) => {
        setCurrentPage(v);
  };

  return (
    <>
      <LoadingSpinner isLoading={isLoading} />
      <Title title="トレーニング一覧" />
      <div className="plate">
        <div className="on_same_row justify" style={{ maxWidth: maxWidth }}>
          <div className="on_same_row flex-wrap">
            {userInfo?.user_type == HEADQUARTER_USER &&
              <div className="item">
                <Select value={getSelectValue(search.facility_id, fcForSelect)} options={fcForSelect} className="H001_select" placeholder="施設"
                  onChange={(e: any) => {
                    let newSearch = { ...search };
                    newSearch.facility_id = e.value;
                    newSearch.child_id = 0
                    setSearch(newSearch);
                    setGetChildForSelect(true);
                                      }}
                />
              </div>
            }
            {search.facility_id > 0 &&
              <div className="item">
                <Select value={getSelectValue(search.child_id, childForSelect)}
                  options={childForSelect} className="H001_select" placeholder="お子様"
                  onChange={(e: any) => {
                    setSearch({ ...search, child_id: e.value });
                                      }}
                />
              </div>
            }
            <div className="on_same_row item">
              <CustomDate val={search.begin_date} setter={(v: string) => {
                setSearch({ ...search, begin_date: v });
                              }} />
              <div className="wave_str">～</div>
              <CustomDate val={search.end_date} setter={(v: string) => {
                setSearch({ ...search, end_date: v });
                              }} />
            </div>
            <div className="item">
              <Select value={getSelectValue(search.absence_type, absenceTypeForSelect)}
                options={absenceTypeForSelect} className="H001_select" placeholder="出欠タイプ"
                onChange={(e: any) => {
                  setSearch({ ...search, absence_type: e.value });
                                  }}
              />
            </div>
            <div className="item">
              <Select value={getSelectValue(search.status, statusForSelect)}
                options={statusForSelect} className="H001_select" placeholder="実施状況"
                onChange={(e: any) => {
                  setSearch({ ...search, status: e.value });
                                  }}
              />
            </div>
          </div>
          <CustomButton
            label="＋トレーニングを追加"
            min_width={200}
            class={[CB_CLASS.RED]}
            onClick={(e: React.MouseEvent) => moveByClick(e, getForHistoryPush(H002Url, [0, 0, "H001"]))}
          />
        </div>
      </div>
      <PaginationTableLite
        currentPage={currentPage}
        setCurrentPage={changeCurrentPage}
        recordCount={recordCount}
        limit={limitCount}
        max_width={maxWidth}
        headers={
          userInfo?.user_type == HEADQUARTER_USER
            ? ["施設名", "お子様", "保護者", "実施日時", "出欠タイプ", "実施状況"]
            : ["お子様", "保護者", "実施日時", "出欠タイプ", "実施状況"]
        }
        list={data.map((x, i) => (
          <tr key={i} onClick={(e: React.MouseEvent) => {
            if ([TRAINING_STATUS_NOT_TRAINED, TRAINING_STATUS_NOT_PLANNED].indexOf(x.status) > -1) {
              moveByClick(e, getForHistoryPush(H002Url, [x.id, 0, "H001"]));
            } else {
              moveByClick(e, getForHistoryPush(H003Url, [x.id, "H001"]));
            }
          }}>
            {userInfo?.user_type == HEADQUARTER_USER &&
              <td className="item hide_over" style={{ maxWidth: 200 }}>
                {x.facility_name}
              </td>
            }
            <td className="item" valign="middle" align="center">
              <div className="on_same_row justify_center">
                <User
                  url={x.child_profile_pic_url}
                  func={(e: React.MouseEvent) => moveByClick(e, getForHistoryPush(D002Url, [x.child_id]))}
                />
                <div className="item">
                  {x.child_name}
                </div>
              </div>
            </td>
            <td className="item" valign="middle" align="center">
              <div className="on_same_row justify_center">
                <User
                  url={x.parent_profile_pic_url}
                  func={(e: React.MouseEvent) => moveByClick(e, getForHistoryPush(C002Url, [x.parent_id]))}
                />
                <div className="item">
                  {x.parent_name}
                </div>
                <Chat user_type={userInfo?.user_type} func={(e: React.MouseEvent) => moveByClick(e, getForHistoryPush(G001Url, [x.parent_id, PARENT]))} />
              </div>
            </td>
            <td className="item" >
              {x.absence_type == SERVICE_PROVISION_STATUS.ABSENCE || x.absence_type == SERVICE_PROVISION_STATUS.ABSENCE_1 ? (
                makeStringDateWithSlash(x.training_date)
              ) : (
                `${makeStringDateWithSlash(x.training_date)} ${x.start_time}-${x.end_time}`
              )}
            </td>
            <td className="item" valign="middle" align="center">
              {getSelectValue(x.absence_type ?? 0, absenceTypeForSelect)?.label}
            </td>
            <td className="item" valign="middle" align="center">
              <Status
                label_list={statusForSelect}
                color_list={status_list}
                status={x.status}
                max_width={150}
              />
            </td>
          </tr>
        ))}
      />
    </>
  );
};

export default H001;
